/**
 * Layout — navigation configuration (NAV_TOP + NAV_GROUPS).
 *
 * Extracted from `Layout.jsx`. Pure data — no React, no behavior.
 * The icon references are passed through as identifiers; Layout.jsx
 * still receives them and renders. Keeping icons here too means the
 * single source of truth for ordering / permissions / feature flags
 * lives in one place.
 */
import {
  LayoutDashboard, Target, Users, Package, FileSpreadsheet, Truck,
  ScrollText, FolderOpen, Inbox, Banknote, RefreshCw,
  BarChart3, Settings2, ShieldAlert, ListChecks, Globe, Wallet,
  FileText, Building2, CalendarDays, Clock, ReceiptText, BookOpen, MapPin,
  Award, UserCog, LineChart, Gauge, Radar,
} from "lucide-react";

export const NAV_TOP = [
  // Both top items belong to the Certification ERP — hidden for HR-only plans.
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: "*", feature: null, module: "certification" },
  { to: "/my-work", label: "My Work", icon: ListChecks, testid: "nav-my-work", roles: "*", feature: null, module: "certification" },
];

export const NAV_GROUPS = [
  {
    key: "business_development", label: "Business Development", module: "certification",
    items: [
      { to: "/leads", label: "Inquiries", icon: Target, testid: "nav-leads", roles: ["Owner", "Admin", "Sales Manager", "Sales Executive"], feature: "module.leads" },
      { to: "/quotations", label: "Quotations", icon: FileSpreadsheet, testid: "nav-quotations", roles: ["Owner", "Admin", "Sales Manager", "Sales Executive", "Renewal Manager"], feature: "module.leads" },
      { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers", roles: ["Owner", "Admin", "Sales Manager", "Sales Executive", "Operations Manager", "Consultant", "Document Controller", "Finance Manager", "Finance User", "Renewal Manager", "Trainer", "PRO Executive"], feature: null },
      // 2026-08-29 · Vendor master — parallel to Customers.  Same
      // role set as Customers minus Sales-only roles (vendors are a
      // finance / operations / procurement concern, not a sales
      // deliverable).  Any vendor created here flows into every
      // `/vendors` dropdown in the app (Vendor Bills, Expenses,
      // HR Visits).
      { to: "/vendors", label: "Vendors", icon: Truck, testid: "nav-vendors", roles: ["Owner", "Admin", "Operations Manager", "Consultant", "Document Controller", "Finance Manager", "Finance User", "PRO Executive"], feature: null },
      // 2026-08-29 · Authority master — mirror of Vendors but scoped
      // to regulatory bodies (MoIAT, ESMA, MoHAP, ADAFSA …).  Any
      // authority created here flows into every `/authorities`
      // dropdown (Services default_authority_id, Quotations,
      // Purchase Order supplier picker).
      { to: "/authorities", label: "Authorities", icon: Building2, testid: "nav-authorities", roles: ["Owner", "Admin", "Operations Manager", "Consultant", "Document Controller", "Finance Manager", "Finance User", "PRO Executive"], feature: null },
      { to: "/products", label: "SKU", icon: Package, testid: "nav-products", roles: ["Owner", "Admin", "Sales Manager", "Sales Executive", "Operations Manager", "Compliance Reviewer", "Consultant", "Document Controller", "Renewal Manager", "Trainer", "PRO Executive"], feature: null },
    ],
  },
  {
    key: "operations", label: "Operations", module: "certification",
    items: [
      { to: "/cases", label: "Products", icon: ScrollText, testid: "nav-cases-tree", kind: "tree", roles: ["Owner", "Admin", "Sales Manager", "Sales Executive", "Operations Manager", "Consultant", "Compliance Reviewer", "Document Controller", "Finance Manager", "Finance User", "Renewal Manager", "Trainer", "PRO Executive"], feature: "module.cases" },
      { to: "/documents", label: "Documents", icon: FolderOpen, testid: "nav-documents", roles: ["Owner", "Admin", "Operations Manager", "Consultant", "Compliance Reviewer", "Document Controller", "Finance Manager", "Finance User", "Trainer", "PRO Executive"], feature: "module.documents" },
      { to: "/submissions", label: "Submissions", icon: Inbox, testid: "nav-submissions", roles: ["Owner", "Admin", "Operations Manager", "Consultant", "Compliance Reviewer", "Document Controller", "PRO Executive"], feature: "module.documents" },
      { to: "/surveillance", label: "Surveillance", icon: Radar, testid: "nav-surveillance", roles: ["Owner", "Admin", "Operations Manager", "Consultant", "Compliance Reviewer", "Document Controller", "Renewal Manager", "PRO Executive"], feature: "module.cases" },
    ],
  },
  {
    key: "commercial_control", label: "Commercial Control", module: "certification",
    items: [
      { to: "/finance", label: "Finance", icon: Banknote, testid: "nav-finance", roles: ["Owner", "Admin", "Finance Manager", "Finance User"], feature: "module.finance" },
      { to: "/renewals", label: "Renewals", icon: RefreshCw, testid: "nav-renewals", roles: ["Owner", "Admin", "Sales Manager", "Sales Executive", "Operations Manager", "Consultant", "Document Controller", "Finance Manager", "Renewal Manager", "Trainer"], feature: "module.renewals" },
    ],
  },
  {
    key: "insights", label: "Insights",
    items: [
      { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", roles: ["Owner", "Admin", "Sales Manager", "Operations Manager", "Compliance Reviewer", "Finance Manager", "Finance User", "Renewal Manager", "Trainer", "PRO Executive", "Viewer", "Auditor"], feature: null },
      // CertOpz Analytics Pack (2026-02) — executive scorecard +
      // full analytics workspace. Same audience as /reports; the
      // backend re-checks per-report scope & permission (PEOPLE
      // reports still require `hr.reports.read`).
      { to: "/analytics/scorecard", label: "Executive Scorecard", icon: Gauge, testid: "nav-analytics-scorecard", roles: ["Owner", "Admin", "Finance Manager", "Operations Manager"], feature: "module.reports" },
      { to: "/analytics", label: "Analytics Workspace", icon: LineChart, testid: "nav-analytics-workspace", roles: ["Owner", "Admin", "Sales Manager", "Operations Manager", "Compliance Reviewer", "Finance Manager", "Finance User", "Renewal Manager", "PRO Executive", "Viewer", "Auditor"], feature: "module.reports" },
    ],
  },
  {
    key: "external_access", label: "External Access",
    items: [
      { to: "/portal", label: "Client Portal", icon: Globe, testid: "nav-portal", roles: ["Client User"], feature: "collab.client_portal" },
    ],
  },
  {
    key: "configuration", label: "Configuration",
    items: [
      { to: "/masters", label: "Masters", icon: Settings2, testid: "nav-masters", roles: ["Owner", "Admin", "Operations Manager", "Compliance Reviewer"], feature: null },
      { to: "/admin", label: "Admin Settings", icon: ShieldAlert, testid: "nav-admin", roles: ["Owner", "Admin"], feature: null },
    ],
  },
  {
    key: "hr_payroll", label: "HR & Payroll", module: "hr",
    items: [
      { to: "/hr/dashboard", label: "HR Dashboard", icon: LayoutDashboard, testid: "nav-hr-dashboard", hrPerm: "hr.dashboard.read", feature: null },
      { to: "/hr/employees", label: "Employees", icon: Users, testid: "nav-hr-employees", hrPerm: "hr.employees.read", feature: null },
      { to: "/hr/salary-readiness", label: "Salary Readiness", icon: ShieldAlert, testid: "nav-hr-salary-readiness", hrPerm: "hr.salary_structures.read", feature: null },
      { to: "/hr/salary-structures", label: "Salary Structures", icon: Banknote, testid: "nav-hr-salary-structures", hrPerm: "hr.salary_structures.read", feature: null },
      { to: "/hr/payroll-runs", label: "Payroll Runs", icon: Wallet, testid: "nav-hr-payroll-runs", hrPerm: "hr.payroll.read", feature: null },
      { to: "/hr/payslip-template", label: "Payslip Template", icon: FileText, testid: "nav-hr-payslip-template", hrPerm: "hr.payslip_templates.read", feature: null },
      { to: "/hr/masters", label: "HR Master Data", icon: Building2, testid: "nav-hr-masters", hrPermAny: ["hr.departments.read", "hr.designations.read"], feature: null },
      { to: "/hr/profile-change-requests", label: "Profile Change Requests", icon: Inbox, testid: "nav-hr-profile-change-requests", hrPerm: "hr.employees.update", feature: null },
      { to: "/hr/leave", label: "Leave Management", icon: CalendarDays, testid: "nav-hr-leave", hrPerm: "hr.leave.read", feature: null },
      { to: "/hr/attendance", label: "Attendance", icon: Clock, testid: "nav-hr-attendance", hrPerm: "hr.attendance.read", feature: null },
      { to: "/hr/reimbursements", label: "Reimbursements", icon: ReceiptText, testid: "nav-hr-reimbursements", hrPerm: "hr.reimbursements.read", feature: null },
      { to: "/hr/tax-documents", label: "Tax Documents", icon: FileText, testid: "nav-hr-tax-documents", hrPerm: "hr.tax.read", feature: null },
      { to: "/hr/policies", label: "Policies", icon: BookOpen, testid: "nav-hr-policies", hrPerm: "hr.policies.read", feature: null },
      // HR-5G — Visit logs (customer / authority / vendor / site).
      { to: "/hr/visits", label: "Visit Logs", icon: MapPin, testid: "nav-hr-visits", hrPerm: "hr.visits.read", feature: null },
      { to: "/hr/reports", label: "Reports", icon: BarChart3, testid: "nav-hr-reports", hrPerm: "hr.reports.read", feature: null },
      { to: "/hr/settings", label: "HR Settings", icon: Settings2, testid: "nav-hr-settings", hrPermAny: ["hr.salary_components.read", "hr.salary_components.manage", "hr.pay_cycles.read", "hr.pay_cycles.manage"], feature: null },
    ],
  },
  {
    key: "my_hr", label: "My HR", module: "hr",
    items: [
      { to: "/my-hr/profile", label: "My Profile", icon: Users, testid: "nav-my-hr-profile", hrPerm: "hr.self_service", feature: null },
      { to: "/my-hr/payslips", label: "My Payslips", icon: FileText, testid: "nav-my-hr-payslips", hrPerm: "hr.payslips.self_read", feature: null },
      { to: "/my-hr/leave", label: "My Leave", icon: CalendarDays, testid: "nav-my-hr-leave", hrPerm: "hr.leave.self", feature: null },
      { to: "/my-hr/attendance", label: "My Attendance", icon: Clock, testid: "nav-my-hr-attendance", hrPerm: "hr.attendance.self", feature: null },
      // HR-5G — Employee self-service visit logs.
      { to: "/my-hr/visits", label: "My Visits", icon: MapPin, testid: "nav-my-hr-visits", hrPerm: "hr.visits.self", feature: null },
    ],
  },
];

// Phase SIDEBAR-REGROUP (2026-07-09) — Two-level nested sidebar layout.
//
// All existing NAV_GROUPS are the SAME (order, items, roles, features,
// module gating — unchanged). Sections wrap those groups so the Owner
// sidebar reads as:
//   Dashboard · My Work
//   Certifications
//     · Business Development · Operations · Commercial Control · Insights
//     · External Access · Configuration
//   Human Resources
//     · HR & Payroll · My HR
//
// A section is only rendered when at least one of its child groups has
// visible items (child gating already handles module + role + feature +
// hrPerm checks — sections just cascade that outcome to the parent).
//
// The `module` field on a section is optional visual metadata only;
// group-level module gating is the authoritative check.
export const NAV_SECTIONS = [
  {
    key: "certifications",
    label: "Registrations / Certifications",
    icon: Award,
    testid: "nav-section-certifications",
    module: "certification",
    // No section-level module gate — each nested group carries its own
    // module/role/feature check. Sections auto-hide when all children
    // are empty (e.g. HR-only tenant with zero certification access).
    groupKeys: [
      "business_development",
      "operations",
      "commercial_control",
      "insights",
      "external_access",
      "configuration",
    ],
  },
  {
    key: "human_resources",
    label: "Human Resources",
    icon: UserCog,
    testid: "nav-section-human-resources",
    module: "hr",
    // HR module gating is enforced per-group (both `hr_payroll` and
    // `my_hr` carry `module: "hr"`), so this section hides entirely
    // when the tenant does not subscribe to HR.
    groupKeys: ["hr_payroll", "my_hr"],
  },
];
